import React, {
  createContext, useContext, useState, useEffect, useMemo
} from 'react';
import { node } from 'prop-types';

import { usePromoModel } from './PromoModelProvider';
import {
  emptyPromoCart, transformCartModelToPromoCart
} from '../utils/promo-cart-utils';
import { useCart } from '../hooks/useCart';

export const PromoCartContext = createContext();

export const usePromoCart = () => useContext(PromoCartContext);

export const PromoCartProvider = ({ children }) => {

  const {
    isBogo,
    isMsb,
    isBmsm,
    anchorItem,
    rewardTiers,
    allFulfillableProductsAndAnchor,
    singleItemIdToDisplay,
    displayableItemIds,
  } = usePromoModel();
  const {
    CartModel,
    addToCart,
    updateCart,
    isCartLoading,
    setIsCartLoading,
    freezeCart
  } = useCart();

  const [promoCart, setPromoCart] = useState(emptyPromoCart);
  const [bogoSelectedProduct, setBogoSelectedProduct] = useState(null);

  useEffect(() => {
    setBogoSelectedProduct(singleItemIdToDisplay ? allFulfillableProductsAndAnchor.get(singleItemIdToDisplay) : null);
  }, [singleItemIdToDisplay, allFulfillableProductsAndAnchor]);

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('buybox.bogoSelectedProduct', bogoSelectedProduct);
  }, [bogoSelectedProduct]);

  useEffect(() => {
    if (promoCart?.freeItemInCart) {
      setBogoSelectedProduct(null);
    }
  }, [promoCart]);

  useEffect(() => {
    if (CartModel) {
      const newPromoCart = transformCartModelToPromoCart({
        rawCartModel: CartModel,
        isBogo,
        isMsb,
        isBmsm,
        displayableItemIds,
        anchorItemId: anchorItem.itemId,
        bogoSelectedProduct,
        rewardTiers,
        allFulfillableProductsAndAnchor,
      });
      if (newPromoCart) setPromoCart(newPromoCart);
    }
  }, [
    CartModel,
    isBogo,
    isMsb,
    isBmsm,
    anchorItem.itemId,
    bogoSelectedProduct,
    rewardTiers,
    allFulfillableProductsAndAnchor,
    displayableItemIds
  ]);

  const contextValue = useMemo(() => ({
    // promoCart is only mutated as a sideEffect of updating rawCartModel
    promoCartValue: promoCart.promoCartValue,
    promoCartQuantity: promoCart.promoCartQuantity,
    promoCartItemsObj: promoCart.promoCartItemsObj,
    freeItemInCart: promoCart.freeItemInCart,
    promoSuccessLevel: promoCart.promoSuccessLevel,
    // Percentage of promotion completed. For BMSM percentage of current tier completed
    progressMade: promoCart.progressMade,
    isPromoAchieved: promoCart.isPromoAchieved,
    currentPromoTier: promoCart.currentPromoTier,
    bogoSelectedProduct,
    setBogoSelectedProduct,
    CartModel,
    addToCart,
    updateCart,
    freezeCart,
    isCartLoading,
    setIsCartLoading,
  }), [
    promoCart,
    bogoSelectedProduct,
    CartModel,
    addToCart,
    updateCart,
    freezeCart,
    isCartLoading,
    setIsCartLoading,
  ]);

  return (
    <PromoCartContext.Provider value={contextValue}>
      {children}
    </PromoCartContext.Provider>
  );
};

PromoCartProvider.propTypes = {
  children: node.isRequired,
};
